import type { LinkedMeasure, SideFinance } from './InitiativeDetailPage';

/** "#108 Law Enforcement Reporting" — number first, then the popular name when known. */
export const measureLabel = (measure: Pick<LinkedMeasure, 'number' | 'name'>): string =>
  [measure.number, measure.name].filter(Boolean).join(' ') || 'Untitled measure';

const plural = (count: number, word: string) => `${count} ${word}${count === 1 ? '' : 's'}`;

/**
 * The count line under a side's totals (Shared Committee Finance PRD §5.6):
 * "3 committees · 1 dedicated · 1 shared · 1 not finance-linked", zero terms omitted.
 * `warning` marks the terms that call for admin attention.
 */
export function sideCountParts(finance: SideFinance | undefined): Array<{ text: string; warning?: boolean }> {
  const committees = finance?.committee_count ?? 0;
  const dedicated = finance?.dedicated_committee_count ?? 0;
  const shared = finance?.shared_committee_count ?? 0;
  const unlinked = committees - (finance?.linked_committee_count ?? 0);
  const parts: Array<{ text: string; warning?: boolean }> = [{ text: plural(committees, 'committee') }];
  if (dedicated > 0) parts.push({ text: `${dedicated} dedicated` });
  if (shared > 0) parts.push({ text: `${shared} shared` });
  if (unlinked > 0) parts.push({ text: `${unlinked} not finance-linked`, warning: true });
  return parts;
}

/**
 * Why a side's totals are empty, when they are (§5.6): nothing is linked at all
 * (missing data), or everything linked also backs other measures (no money can be
 * attributed to this one alone). Null when the totals are meaningful as shown.
 */
export function emptyTotalsReason(finance: SideFinance | undefined): 'nothing-linked' | 'all-shared' | null {
  if (!finance || finance.committee_count === 0) return null;
  const dedicated = finance.dedicated_committee_count ?? 0;
  const shared = finance.shared_committee_count ?? 0;
  if (dedicated > 0) return null;
  if (shared > 0) return 'all-shared';
  return finance.linked_committee_count === 0 ? 'nothing-linked' : null;
}

export interface CommitteeLinks {
  co_id: string;
  finance_committee_id: string | null;
  purpose: string | null;
  links: Array<LinkedMeasure & { this_measure: boolean }>;
}

/**
 * What the committee dialog should warn about before save (§5.5). `side` is the side
 * being saved on this measure; `committeeId` is the row being edited, if any, so its
 * own existing link isn't reported back as a conflict.
 */
export function committeeLinkWarnings(
  links: CommitteeLinks | null,
  side: 'support' | 'oppose' | null,
  editingSide: string | null = null,
): {
  shared: LinkedMeasure[];
  notCounted: LinkedMeasure[];
  bothSides: boolean;
} {
  if (!links) return { shared: [], notCounted: [], bothSides: false };
  const elsewhere = links.links.filter((link) => !link.this_measure);
  // This committee's links on this measure, minus the row being edited (it's about to
  // be replaced by what's in the dialog); one on the opposite side is a conflict.
  const hereSides = links.links.filter((link) => link.this_measure).map((link) => link.side);
  if (editingSide) {
    const own = hereSides.indexOf(editingSide);
    if (own >= 0) hereSides.splice(own, 1);
  }
  const opposite = side === 'support' ? 'oppose' : side === 'oppose' ? 'support' : null;
  return {
    shared: elsewhere.filter((link) => link.qualifies),
    notCounted: elsewhere.filter((link) => !link.qualifies),
    bothSides: opposite !== null && hereSides.includes(opposite),
  };
}
